// assets/product-custom-fields.js
import { Component } from '@theme/component';

const CUSTOM_VALUE = '__custom__';

/**
 * @typedef {object} CustomFieldValidationError
 * @property {string} heading
 * @property {HTMLElement} field
 * @property {HTMLElement|null} control
 */

/**
 * @typedef {object} CustomFieldValidationResult
 * @property {boolean} valid
 * @property {CustomFieldValidationError[]} errors
 */

/**
 * @param {Document|Element|null|undefined} scope
 * @returns {Element|null}
 */
function findCustomFieldsComponent(scope) {
  const root =
    scope instanceof Element
      ? scope
      : scope instanceof Document
        ? scope.documentElement
        : document;

  return (
    root.querySelector?.('product-custom-fields-component') ||
    root.closest?.('product-custom-fields-component') ||
    root.closest?.('.product-information, .product-details, quick-add-dialog')?.querySelector('product-custom-fields-component') ||
    null
  );
}

/**
 * @param {Element} fieldEl
 * @returns {{ value: string, control: HTMLElement|null }}
 */
function getCustomFieldValue(fieldEl) {
  const fieldType = fieldEl.getAttribute('data-field-type');

  if (fieldType === 'text') {
    const control = fieldEl.querySelector('[data-custom-field-text]');
    return {
      value: control instanceof HTMLInputElement ? control.value.trim() : '',
      control: control instanceof HTMLElement ? control : null,
    };
  }

  if (fieldType === 'select') {
    const control = fieldEl.querySelector('[data-custom-field-select]');
    return {
      value: control instanceof HTMLSelectElement ? control.value.trim() : '',
      control: control instanceof HTMLElement ? control : null,
    };
  }

  if (fieldType === 'select_with_custom') {
    const select = fieldEl.querySelector('[data-custom-field-select]');
    if (!(select instanceof HTMLSelectElement)) {
      return { value: '', control: null };
    }

    const customValue = select.getAttribute('data-custom-value') || CUSTOM_VALUE;

    if (select.value === customValue) {
      const control = fieldEl.querySelector('[data-custom-text]');
      return {
        value: control instanceof HTMLInputElement ? control.value.trim() : '',
        control: control instanceof HTMLElement ? control : null,
      };
    }

    return {
      value: select.value.trim(),
      control: select,
    };
  }

  if (fieldType === 'color') {
    const checked = fieldEl.querySelector('[data-custom-field-color]:checked');
    return {
      value: checked instanceof HTMLInputElement ? checked.value.trim() : '',
      control: checked instanceof HTMLElement ? checked : null,
    };
  }

  return { value: '', control: null };
}

/**
 * @param {Element} fieldEl
 */
function warnColorFieldValidationFailure(fieldEl) {
  const heading = fieldEl.getAttribute('data-field-heading') || 'Field';
  const checked = fieldEl.querySelector('[data-custom-field-color]:checked');

  console.warn('[CustomFields:color] validation failed', {
    heading,
    hasChecked: checked instanceof HTMLInputElement,
    checkedValue: checked instanceof HTMLInputElement ? checked.value : null,
  });
}

/*
function logColorFieldState(fieldEl, label) {
  const heading = fieldEl.getAttribute('data-field-heading') || '(unknown)';
  const radios = Array.from(fieldEl.querySelectorAll('[data-custom-field-color]'));
  const options = radios.map((radio) => {
    if (!(radio instanceof HTMLInputElement)) {
      return { invalid: true };
    }

    return {
      id: radio.id,
      value: radio.value,
      valueEmpty: radio.value.trim() === '',
      checked: radio.checked,
      label:
        radio.closest('label')?.querySelector('.field-option__label')?.textContent?.trim() || '',
    };
  });
  const checked = fieldEl.querySelector('[data-custom-field-color]:checked');
  const { value } = getCustomFieldValue(fieldEl);

  console.info(`[CustomFields:color] ${label}`, {
    heading,
    resolvedValue: value,
    resolvedValueEmpty: value === '',
    hasChecked: checked instanceof HTMLInputElement,
    checkedValue: checked instanceof HTMLInputElement ? checked.value : null,
    optionCount: radios.length,
    options,
  });
}
*/

/**
 * @param {Document|Element|null|undefined} scope
 * @returns {CustomFieldValidationResult}
 */
export function validateCustomFields(scope) {
  const component = findCustomFieldsComponent(scope);

  if (!(component instanceof HTMLElement)) {
    return { valid: true, errors: [] };
  }

  /** @type {CustomFieldValidationError[]} */
  const errors = [];

  component.querySelectorAll('[data-custom-field-required="true"]').forEach((fieldEl) => {
    if (!(fieldEl instanceof HTMLElement)) return;

    const heading = fieldEl.getAttribute('data-field-heading') || 'Field';
    const fieldType = fieldEl.getAttribute('data-field-type');
    const { value, control } = getCustomFieldValue(fieldEl);

    if (fieldType === 'color' && !value) {
      warnColorFieldValidationFailure(fieldEl);
    }

    if (value) {
      fieldEl.classList.remove('custom-field--error');
      return;
    }

    fieldEl.classList.add('custom-field--error');
    errors.push({ heading, field: fieldEl, control });
  });

  return { valid: errors.length === 0, errors };
}

/**
 * @param {Document|Element|null|undefined} scope
 * @returns {Record<string, string>}
 */
export function getCustomFieldValues(scope) {
  const component = findCustomFieldsComponent(scope);

  if (!(component instanceof HTMLElement)) {
    return {};
  }

  /** @type {Record<string, string>} */
  const values = {};

  component.querySelectorAll('.custom-field[data-field-heading]').forEach((fieldEl) => {
    if (!(fieldEl instanceof HTMLElement)) return;

    const heading = fieldEl.getAttribute('data-field-heading');
    if (!heading) return;

    const { value } = getCustomFieldValue(fieldEl);
    if (value) {
      values[heading] = value;
    }
  });

  return values;
}

/**
 * @param {Document|Element|null|undefined} scope
 * @param {Record<string, string>} values
 */
export function applyCustomFieldValues(scope, values) {
  if (!values || typeof values !== 'object') return;

  const component = findCustomFieldsComponent(scope);
  if (!(component instanceof HTMLElement)) return;

  component.querySelectorAll('[data-field-heading]').forEach((fieldEl) => {
    if (!(fieldEl instanceof HTMLElement)) return;

    const heading = fieldEl.getAttribute('data-field-heading');
    if (!heading || !Object.prototype.hasOwnProperty.call(values, heading)) return;

    const value = String(values[heading] ?? '').trim();
    const fieldType = fieldEl.getAttribute('data-field-type');

    if (fieldType === 'text') {
      const input = fieldEl.querySelector('[data-custom-field-text]');
      if (input instanceof HTMLInputElement) {
        input.value = value;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    } else if (fieldType === 'select') {
      const select = fieldEl.querySelector('[data-custom-field-select]');
      if (select instanceof HTMLSelectElement) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (fieldType === 'select_with_custom') {
      const select = fieldEl.querySelector('[data-custom-field-select]');
      if (!(select instanceof HTMLSelectElement)) return;

      const customValue = select.getAttribute('data-custom-value') || CUSTOM_VALUE;
      const matchedOption = Array.from(select.options).find(
        (option) => option.value === value && option.value !== customValue
      );

      if (matchedOption) {
        select.value = value;
      } else if (value) {
        select.value = customValue;
        const customInput = fieldEl.querySelector('[data-custom-text]');
        if (customInput instanceof HTMLInputElement) {
          customInput.value = value;
          customInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

      select.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (fieldType === 'color') {
      const radios = fieldEl.querySelectorAll('[data-custom-field-color]');
      let matched = false;

      radios.forEach((radio) => {
        if (!(radio instanceof HTMLInputElement)) return;
        const isMatch = radio.value === value;
        radio.checked = isMatch;
        if (isMatch) matched = true;
      });

      if (matched) {
        const checked = fieldEl.querySelector('[data-custom-field-color]:checked');
        checked?.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (value) {
      fieldEl.classList.remove('custom-field--error');
    }
  });
}

/**
 * @param {CustomFieldValidationResult} result
 * @param {Document|Element|null|undefined} scope
 * @returns {boolean}
 */
export function reportCustomFieldsValidation(result, scope) {
  if (result.valid) return true;

  const component = findCustomFieldsComponent(scope);
  const firstError = result.errors[0];

  if (firstError?.field) {
    firstError.field.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  if (firstError?.control instanceof HTMLElement) {
    firstError.control.focus({ preventScroll: true });
  }

  const headings = result.errors.map((error) => error.heading).join(', ');
  let message = `Please complete these required fields before uploading: ${headings}.`;

  if (component instanceof HTMLElement) {
    if (result.errors.length === 1) {
      message = (component.dataset.msgRequired || message).replace('[field]', result.errors[0].heading);
    } else {
      message = (component.dataset.msgRequiredMultiple || message).replace('[fields]', headings);
    }
  }

  if (typeof MessageToast !== 'undefined') {
    MessageToast.error(message);
  } else {
    alert(message);
  }

  return false;
}

/**
 * @extends Component
 */
class ProductCustomFields extends Component {
  handleInput(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.matches('[data-custom-field-text], [data-custom-text]')) return;

    input.closest('.custom-field')?.classList.remove('custom-field--error');
    this.#applyInputFilter(input);
    this.#updateCharacterCount(input);
  }

  handleKeydown(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.matches('[data-custom-field-text], [data-custom-text]')) return;

    const filter = input.getAttribute('data-input-filter') || 'text';
    if (filter === 'text' || this.#isControlKey(event)) return;

    const key = event.key;

    if (filter === 'integer') {
      if (!/^\d$/.test(key)) {
        event.preventDefault();
      }
      return;
    }

    if (filter === 'float') {
      if (/^\d$/.test(key)) return;
      if (key === '.' && !input.value.includes('.')) return;
      event.preventDefault();
    }
  }

  handleSelectChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    if (!select.matches('[data-custom-field-select]')) return;

    select.closest('.custom-field')?.classList.remove('custom-field--error');

    const field = select.closest('[data-field-type="select_with_custom"]');
    if (!field) return;

    this.#syncSelectWithCustomField(field, select);
  }

  handleColorChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.matches('[data-custom-field-color]')) return;

    const field = input.closest('.custom-field');
    if (field instanceof HTMLElement) {
      field.classList.remove('custom-field--error');
      // logColorFieldState(field, 'click');
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #isControlKey(event) {
    const controlKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];

    if (controlKeys.includes(event.key)) return true;
    if (event.ctrlKey || event.metaKey) return true;

    return false;
  }

  /**
   * @param {HTMLInputElement} input
   */
  #applyInputFilter(input) {
    const filter = input.getAttribute('data-input-filter') || 'text';
    if (filter === 'text') return;

    const sanitized = this.#sanitizeValue(input.value, filter);
    if (sanitized === input.value) return;

    input.value = sanitized;
  }

  /**
   * @param {string} value
   * @param {string} filter
   */
  #sanitizeValue(value, filter) {
    if (filter === 'integer') {
      return value.replace(/\D/g, '');
    }

    if (filter === 'float') {
      let sanitized = '';
      let hasDecimal = false;

      for (const char of value) {
        if (char >= '0' && char <= '9') {
          sanitized += char;
          continue;
        }

        if (char === '.' && !hasDecimal) {
          hasDecimal = true;
          sanitized += char;
        }
      }

      return sanitized;
    }

    return value;
  }

  /**
   * @param {HTMLInputElement} input
   */
  #updateCharacterCount(input) {
    const wrapper = input.closest('.__input-wrapper');
    const characterCount = wrapper?.querySelector('[data-character-count]');
    if (!(characterCount instanceof HTMLElement)) return;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const maxLength = input.maxLength > 0 ? input.maxLength : Number(characterCount.getAttribute('data-max') || 0);

    characterCount.textContent = template
      .replace('[current]', input.value.length.toString())
      .replace('[max]', maxLength.toString());
  }

  /**
   * @param {Element} field
   * @param {HTMLSelectElement} select
   */
  #syncSelectWithCustomField(field, select) {
    const customPanel = field.querySelector('[data-custom-input]');
    const customInput = field.querySelector('[data-custom-text]');
    const propertyName = field.getAttribute('data-property-name');
    const customValue = select.getAttribute('data-custom-value') || CUSTOM_VALUE;
    const isCustom = select.value === customValue;

    if (!(customPanel instanceof HTMLElement) || !(customInput instanceof HTMLInputElement) || !propertyName) {
      return;
    }

    if (isCustom) {
      customPanel.hidden = false;
      select.removeAttribute('name');
      select.removeAttribute('required');
      select.setAttribute('aria-required', 'false');

      customInput.name = propertyName;
      customInput.setAttribute('aria-required', select.hasAttribute('data-was-required') ? 'true' : 'false');
      if (select.hasAttribute('data-was-required')) {
        customInput.required = true;
      }

      customInput.focus();
      this.#applyInputFilter(customInput);
      this.#updateCharacterCount(customInput);
      return;
    }

    customPanel.hidden = true;
    customInput.value = '';
    customInput.removeAttribute('name');
    customInput.removeAttribute('required');
    customInput.setAttribute('aria-required', 'false');

    select.name = propertyName;
    if (select.hasAttribute('data-was-required')) {
      select.required = true;
      select.setAttribute('aria-required', 'true');
    }

    this.#updateCharacterCount(customInput);
  }

  connectedCallback() {
    super.connectedCallback();

    this.querySelectorAll('[data-field-type="select_with_custom"]').forEach((field) => {
      const select = field.querySelector('[data-custom-field-select]');
      if (!(select instanceof HTMLSelectElement)) return;

      if (select.required) {
        select.setAttribute('data-was-required', 'true');
      }

      this.#syncSelectWithCustomField(field, select);
    });

    this.querySelectorAll('[data-custom-field-text], [data-custom-text]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        this.#applyInputFilter(input);
        this.#updateCharacterCount(input);
      }
    });

    /*
    this.querySelectorAll('[data-field-type="color"]').forEach((field) => {
      if (field instanceof HTMLElement) {
        logColorFieldState(field, 'init');
      }
    });
    */
  }
}

customElements.define('product-custom-fields-component', ProductCustomFields);

window.ProductCustomFields = {
  validate: validateCustomFields,
  report: reportCustomFieldsValidation,
  getValues: getCustomFieldValues,
  applyValues: applyCustomFieldValues,
  guard(scope) {
    const result = validateCustomFields(scope);
    if (result.valid) return true;
    reportCustomFieldsValidation(result, scope);
    return false;
  },
};
